import React from 'react';
import { PHONE_DISPLAY, PHONE_HREF, LOCALITY } from '../../config/business';
import { Link } from 'react-router-dom';
import { Button } from '@chakra-ui/react';
import Icon from '../ui/Icon';
import { useHasPublishedNews } from '../../pages/marketing/useNews';
import './LandingFooter.css';

/**
 * Shared site footer used across every marketing page (Landing/home, Contact,
 * Products, Bio-Ply, Team, and the order/enrollment forms).
 * Centralizes the footer the same way LandingHeader centralizes the top
 * navigation, so all pages stay visually identical.
 *
 * @param {string} [tagline] - Short brand line under the logo.
 * @param {Array<{heading: string, links: Array<{label: string, href: string}>}>} [columns]
 *   Footer navigation columns. Defaults to the canonical site links. Pages
 *   with their own in-page section anchors (e.g. Bio-Ply's #what-is) pass a
 *   custom set so those jump links are preserved. A link whose `href` is
 *   exactly `#request-access` routes to the customer enrollment form at
 *   `/enroll` (the single access-request path); an `href` starting with `#`
 *   is an in-page anchor; anything else is a router link.
 * @param {{label: string, to: string}} [brandCta] - Optional extra call to
 *   action rendered under the brand column (e.g. the home page's "Learn
 *   about BIO-PLY™" button). Omitted by default.
 *
 * A link marked `newsOnly: true` (the News link) only shows once at least one
 * news post is published.
 */

export const DEFAULT_FOOTER_COLUMNS = [
  {
    heading: 'Products',
    links: [
      { label: 'All products', href: '/products' },
      { label: 'BIO-PLY™', href: '/products/bio-ply' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', href: '/#about' },
      { label: 'Our Team', href: '/about/team' },
      { label: 'News', href: '/news', newsOnly: true },
      { label: 'Contact', href: '/contact' },
    ],
  },
  {
    heading: 'Support',
    links: [
      { label: 'Login', href: '/login' },
      { label: 'Request access', href: '#request-access' },
    ],
  },
];

export default function LandingFooter({
  tagline = 'Harnessing the power of peptides to advance equine health.',
  columns = DEFAULT_FOOTER_COLUMNS,
  brandCta,
}) {
  const hasNews = useHasPublishedNews();
  const renderLink = (link) => {
    // Legacy sentinel kept so pages passing custom columns don't all need
    // editing: requesting access is the enrollment form and nothing else.
    if (link.href === '#request-access') {
      return (
        <Link key={link.label} to="/enroll">
          {link.label}
        </Link>
      );
    }
    // In-page anchors (e.g. #what-is) stay plain <a> so the browser jumps
    // to the section on the current page.
    if (link.href.startsWith('#')) {
      return (
        <a key={link.label} href={link.href}>
          {link.label}
        </a>
      );
    }
    return (
      <Link key={link.label} to={link.href}>
        {link.label}
      </Link>
    );
  };

  return (
    <footer className="landing-footer" role="contentinfo">
      <div className="landing-footer-accent" aria-hidden="true" />
      <div className="landing-footer-main">
        <div className="landing-footer-brand">
          <div className="landing-footer-logo">
            <img src="/images/Logo-white%20(1).png" alt="Qentoros" />
          </div>
          <p className="landing-footer-tagline">{tagline}</p>
          <ul className="landing-footer-contact" aria-label="Contact Qentoros">
            <li>
              <Icon name="mail_outline" />
              <Link to="/contact">Send us a message</Link>
            </li>
            <li>
              <Icon name="call" />
              <a href={PHONE_HREF}>{PHONE_DISPLAY}</a>
            </li>
            <li>
              <Icon name="place" />
              <span>{LOCALITY}</span>
            </li>
          </ul>
          <div className="landing-footer-social" aria-label="Qentoros on social media">
            <a
              href="https://www.linkedin.com/company/qentoros"
              target="_blank"
              rel="noopener noreferrer"
              className="landing-footer-social-link"
              aria-label="Qentoros on LinkedIn"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.55V9h3.57v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.21 0 22.23 0z"
                />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/qentoros/"
              target="_blank"
              rel="noopener noreferrer"
              className="landing-footer-social-link"
              aria-label="Qentoros on Instagram"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.43.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.43-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.38C1.35 2.67.94 3.35.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.67 1.34 1.08 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.38.67-.67 1.08-1.34 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.38-2.13-.67-.67-1.34-1.08-2.13-1.38-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"
                />
                <circle fill="currentColor" cx="18.41" cy="5.59" r="1.44" />
              </svg>
            </a>
            <a
              href="https://www.facebook.com/qentoros/"
              target="_blank"
              rel="noopener noreferrer"
              className="landing-footer-social-link"
              aria-label="Qentoros on Facebook"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.01 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.96h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.08 24 18.09 24 12.07z"
                />
              </svg>
            </a>
            <Link
              to="/contact"
              className="landing-footer-social-link"
              aria-label="Contact Qentoros"
            >
              <Icon name="mail_outline" />
            </Link>
          </div>
          {brandCta && (
            <Button as={Link} to={brandCta.to} size="sm" className="landing-footer-cta">
              {brandCta.label}
            </Button>
          )}
        </div>
        <nav className="landing-footer-links" aria-label="Footer navigation">
          {columns.map((column) => (
            <div className="landing-footer-column" key={column.heading}>
              <h4>{column.heading}</h4>
              {column.links.filter((link) => !link.newsOnly || hasNews).map(renderLink)}
            </div>
          ))}
        </nav>
      </div>
      <div className="landing-footer-bottom">
        <div className="landing-footer-bottom-left">
          <p className="landing-footer-copyright">
            © {new Date().getFullYear()} Qentoros
          </p>
          <span className="landing-footer-bottom-sep" aria-hidden="true">·</span>
          <p className="landing-footer-built">
            Built in Blacksburg, VA by{' '}
            <a
              href="https://neutrinodesign.com"
              target="_blank"
              rel="noopener noreferrer"
              className="landing-footer-neutrino"
            >
              <span>Neutrino Design</span>
            </a>
          </p>
        </div>
        <div className="landing-footer-legal">
          <a href="/#">Privacy Policy</a>
          <span className="landing-footer-legal-sep" aria-hidden="true">
            ·
          </span>
          <a href="/#">Terms of Service</a>
        </div>
      </div>
    </footer>
  );
}
